<script setup>
/* eslint-disable no-unused-vars */
/* global defineProps, defineEmits */
import defaultEmployeeImage from "@/assets/images/user.png";
import userBlueIcon from "@/assets/icons/user-blue.png";
import userYellowIcon from "@/assets/icons/user-yellow.png";
import IconBlueBox from "@/components/icons/IconBlueBox.vue";
import IconVehicle from "@/components/icons/IconVehicle.vue";
import IconMeetingRoomReservation from "@/components/icons/IconMeetingRoomReservation.vue";
import IconOwnerReport from "@/components/icons/IconOwnerReport.vue";
import auth from "../../api/auth/auth";
import { TurnOff } from "@element-plus/icons-vue";

import { computed, ref, watch } from "vue";
import { useAuthStore } from "@/app/stores/authStore";
import { useTeamHolidaysStore } from "@/app/stores/teamHolidaysStore";
import { useRoute } from "vue-router";
import {
  canAccessSecurityPermissions,
  canAccessSecurityApiApplications,
  canAccessSecurityAudit,
  canAccessImpersonations,
  canAccessSecurityDashboard,
} from "@/utils/securityAccess";

const emit = defineEmits(["close", "closeSide"]);

const authStore = useAuthStore();
const teamStore = useTeamHolidaysStore();

const route = useRoute();

const folder = ref("folder");

const nav_desp = ref(true);

const width_nav = ref(250);

const openHome = ref(false);

const openRRHH = ref(false);

const openProduccion = ref(false);

const openComunication = ref(false);

const openAdministration = ref(false);

const openSecurity = ref(false);

const openSocialFund = ref(false);

const openHostings = ref(false);

const openSettings = ref(false);

const settingsHover = ref(false);

const baseUrl = ref(process.env.VUE_APP_BASE_URI);
const userPhotoLoadError = ref(false);

const user = computed(() => authStore.getUserLogged);

watch(() => user.value?.id, async (id) => {
  try {
    await teamStore.loadEmployee(id);
  } catch {
    // Fail closed: no menu entry until team membership can be checked.
  }
}, { immediate: true });

const isAdmin = computed(() => {
  return !!user.value?.roles?.some((role) =>
    ["ROLE_ADMIN", "ROLE_SYSTEMS", "ROLE_CUSTOMER_ADMIN"].includes(role)
  );
});

const userPhotoUrl = computed(() => {
  if (!user.value?.photo || userPhotoLoadError.value) {
    return defaultEmployeeImage;
  }
  return baseUrl.value + user.value.photo;
});

watch(
  () => user.value?.photo,
  () => {
    userPhotoLoadError.value = false;
  }
);

const isCollaborator = computed(() => {
  return !!user.value?.roles?.includes("ROLE_EXTERNAL_COLLABORATOR");
});

const sidebarHome = computed(() => {
  if (!route.path.includes("/home")) {
    return require(`@/assets/icons/home-blue.png`);
  } else {
    return require(`@/assets/icons/home-yellow.png`);
  }
});

function showRRHH() {
  toggleGroup(openRRHH);
}

function showComunication() {
  toggleGroup(openComunication);
}

function showAdministration() {
  toggleGroup(openAdministration);
}

function showSecurity() {
  toggleGroup(openSecurity);
}

function showSocialFund() {
  toggleGroup(openSocialFund);
}

function showProduccion() {
  toggleGroup(openProduccion);
}

function showSettings() {
  toggleGroup(openSettings);
  settingsHover.value = openSettings.value;
}

function showHostings() {
  toggleGroup(openHostings);
}

function closeGroups() {
  openHome.value = false;
  openRRHH.value = false;
  openProduccion.value = false;
  openComunication.value = false;
  openAdministration.value = false;
  openSecurity.value = false;
  openSocialFund.value = false;
  openHostings.value = false;
  openSettings.value = false;
  settingsHover.value = false;
}

function toggleGroup(groupRef) {
  const nextValue = !groupRef.value;
  closeGroups();
  groupRef.value = nextValue;
  if (nextValue && !nav_desp.value) {
    changeNav();
  }
}

const submenuTransition =
  "height 520ms cubic-bezier(0.22, 1, 0.36, 1), margin-bottom 520ms cubic-bezier(0.22, 1, 0.36, 1), opacity 520ms ease, transform 520ms cubic-bezier(0.22, 1, 0.36, 1)";

function beforeSubmenuEnter(el) {
  el.style.height = "0";
  el.style.marginBottom = "0";
  el.style.opacity = "0";
  el.style.transform = "translateY(-6px)";
}

function enterSubmenu(el) {
  el.style.transition = submenuTransition;
  requestAnimationFrame(() => {
    el.style.height = `${el.scrollHeight}px`;
    el.style.marginBottom = "10px";
    el.style.opacity = "1";
    el.style.transform = "translateY(0)";
  });
}

function afterSubmenuEnter(el) {
  el.style.height = "auto";
  el.style.marginBottom = "";
  el.style.transition = "";
  el.style.opacity = "";
  el.style.transform = "";
}

function beforeSubmenuLeave(el) {
  el.style.height = `${el.scrollHeight}px`;
  el.style.marginBottom = "10px";
  el.style.opacity = "1";
  el.style.transform = "translateY(0)";
}

function leaveSubmenu(el) {
  // Force layout so the browser starts from the measured height before collapsing.
  void el.offsetHeight;
  el.style.transition = submenuTransition;
  requestAnimationFrame(() => {
    el.style.height = "0";
    el.style.marginBottom = "0";
    el.style.opacity = "0";
    el.style.transform = "translateY(-6px)";
  });
}

function afterSubmenuLeave(el) {
  el.style.height = "";
  el.style.marginBottom = "";
  el.style.transition = "";
  el.style.opacity = "";
  el.style.transform = "";
}

function changeFolder() {
  folder.value = "folder";
}

function changeNav() {
  const nextNavState = !nav_desp.value;
  nav_desp.value = nextNavState;
  nav_desp.value ? (width_nav.value = 250) : (width_nav.value = 110);
  if (!nextNavState) {
    requestAnimationFrame(closeGroups);
  }
  localStorage.setItem("sidebarState", JSON.parse(nav_desp.value));
  emit("close", nav_desp.value);
}

function hasRoleUser() {
  return hasRole("ROLE_USER");
}

function hasRoleAdmin() {
  return hasRole("ROLE_ADMIN");
}

function hasRoleSystems() {
  return hasRole("ROLE_SYSTEMS");
}

function hasRole(role) {
  return !!user.value?.roles?.includes(role);
  // const fakeUserRol = ['ROLE_USER'];
  // return fakeUserRol.includes(role);
}

function hasCoursesPermission() {
  const allowedEmails = [
    "jgarcia@undanet.com",
    "faguilar@undanet.com",
    "jcamacho@undanet.com",
    "andrearodriguez@undanet.com",
  ];
  return allowedEmails.includes((user.value?.email || "").toLowerCase());
}

function hasTrackernetPermission() {
  return !!user.value?.canCreateTimeEntry || !!userPermissionsList().includes("trackernet:use");
}

function hasTrackernetReportPermission() {
  return !!user.value?.accessTrackerNetReports || !!userPermissionsList().includes("trackernet:reports");
}

function hasVehicleModulePermission() {
  return !!(user.value?.accessVehicleRequests || user.value?.accessVehicleModule) || userPermissionsList().includes("vehicles:use");
}

function hasSocialFundPermission() {
  return !!user.value?.permissions?.includes("fondo_social:view_all");
}

function hasSocialFundManagePermission() {
  // Restricción de negocio (igual que Facturación): ROLE_ADMIN NO da acceso
  // por sí solo a Fondo Social — hace falta fondo_social:manage concedido
  // explícitamente.
  return !!user.value?.permissions?.includes("fondo_social:manage");
}

function hasInternalTicketsPermission() {
  return hasRoleAdmin() || hasRoleSystems() || !!user.value?.accessInternalTickets || userPermissionsList().includes("tickets:internal");
}

function hasInvoiceModulePermission() {
  // Restricción de negocio: a diferencia del resto de módulos, ROLE_ADMIN NO
  // da acceso por sí solo a Facturación — solo quien tenga invoices:view o
  // invoices:manage concedido explícitamente.
  const permissions = Array.isArray(user.value?.permissions) ? user.value.permissions : [];
  return permissions.includes("invoices:view") || permissions.includes("invoices:manage");
}

function userPermissionsList() {
  return Array.isArray(user.value?.permissions) ? user.value.permissions : [];
}

function hasAnySecurityPermission() {
  return canAccessSecurityDashboard(user.value?.roles, userPermissionsList());
}

function hasSecurityPermissionsModulePermission() {
  return canAccessSecurityPermissions(user.value?.roles, userPermissionsList());
}

function hasSecurityApiApplicationsPermission() {
  return canAccessSecurityApiApplications(user.value?.roles, userPermissionsList());
}

function hasSecurityAuditPermission() {
  return canAccessSecurityAudit(user.value?.roles, userPermissionsList());
}

function hasImpersonationsPermission() {
  return canAccessImpersonations(user.value?.roles, userPermissionsList());
}

function hasSystemsMenuAccess() {
  return hasRoleAdmin() || hasRoleSystems() || hasRoleUser();
}

function eventsResolution(obj, event) {
  console.log("over", obj, event);
  if (screen.width < 768 && event === "click") {
    return !obj;
  } else if (screen.width >= 768 && event === "hover") {
    return !obj;
  }
  return obj;
}

function getUser() {
  return user.value;
}

function useDefaultUserPhoto() {
  userPhotoLoadError.value = true;
}

function onLinkClick(e) {
  const link = e.target.closest("a");
  if (link) {
    emit("closeSide");
  }
}

function openLinkInNewTab(link) {
  window.open(link, "_blank");
}

function logout() {
  auth.logout();
  emit("closeSide");
}

{
  const storage = localStorage.getItem("sidebarState");
  if (storage != null) {
    nav_desp.value = JSON.parse(storage);
    nav_desp.value ? (width_nav.value = 250) : (width_nav.value = 110);
  }
}
</script>

<template>
  <div v-if="user" id="nav" :class="{ 'is-collapsed': !nav_desp }" :style="{ width: width_nav + 'px' }">
    <div class="toggle-button" @click="changeNav">
      <font-awesome-icon :icon="nav_desp ? 'angle-left' : 'angle-right'" class="iconNav" />
    </div>
    <!--    <div id="nav" v-bind:style="{ width: width_nav + 'px'}">-->
    <div class="user" :class="{ closed: !nav_desp }">
      <!-- <div id="circle"></div> -->
      <div class="img-user" :class="nav_desp ? '' : 'closed'">
        <img class="rounded-circle img-fluid thumbnail" :src="userPhotoUrl" :alt="'Employee image'"
          :title="'Click para ampliar'" @error="useDefaultUserPhoto" />
      </div>
      <div class="username" :class="nav_desp ? '' : 'closed'">
        <p style="font-weight: bold; color: #ffffff">
          {{ user.name }} {{ user.lastName }}
        </p>
        <p style="font-weight: bold; color: #b8b8c4">
          {{ user.jobName }}
        </p>
      </div>
    </div>

    <!--    <el-menu class="el-menu-vertical-demo" background-color="transparent">-->
    <!--      <el-menu-item>-->
    <!--        <div @click="showHome()">-->
    <!--          <router-link :to="{name: 'home'}">-->
    <!--            <img style="width: 25px; margin-right: 10px; margin-bottom: 5px;"-->
    <!--                 :src="!openHome ? require(`@/assets/icons/home-blue.png`) : require(`@/assets/icons/home-yellow.png`) "/>-->
    <!--            <span class="pointer">INICIO</span>-->
    <!--          </router-link>-->
    <!--        </div>-->
    <!--      </el-menu-item>-->
    <!--    </el-menu>-->

    <ul class="main-menu" @click="onLinkClick">
      <!-- Inicio -->
      <li class="li-border li-group list-menu" v-if="!isCollaborator">
        <div>
          <router-link :to="{ name: 'home' }">
            <img style="width: 25px; margin-right: 10px; margin-bottom: 5px" :src="sidebarHome" />
            <span class="pointer label">INICIO</span>
          </router-link>
        </div>
      </li>
      <!-- TRACKERNET Colaborador -->
      <li class="li-border li-group list-menu" v-if="isCollaborator">
        <div>
          <router-link :to="{ name: 'tracker' }">
            <img style="width: 25px; margin-right: 10px; margin-bottom: 5px"
              :src="require(`@/assets/icons/tracker-net-blue.png`)" />
            <span>TrackerNet</span>
          </router-link>
        </div>
      </li>
      <!-- RRHH -->
      <li class="li-border li-group list-menu has-submenu" :class="{ 'is-open': openRRHH }" v-if="!isCollaborator">
        <div @click="showRRHH()">
          <!--          <font-awesome-icon class="iconNav" :icon="openRRHH ? 'folder-open' : 'folder'"/>-->
          <img style="width: 25px; margin-right: 10px; margin-bottom: 5px" :src="openRRHH ? userYellowIcon : userBlueIcon" />
          <span class="pointer label">TALENTO</span>
        </div>
        <Transition name="sidebar-submenu" @before-enter="beforeSubmenuEnter" @enter="enterSubmenu"
          @after-enter="afterSubmenuEnter" @before-leave="beforeSubmenuLeave" @leave="leaveSubmenu"
          @after-leave="afterSubmenuLeave">
          <div v-if="openRRHH" class="submenu-border">
            <ul class="sub_menu">
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'absences' }">
                  <font-awesome-icon class="iconNav" icon="list" />
                  <span>Absentismo</span>
                </router-link>
              </li>
              <li v-if="hasRoleUser() || hasRoleAdmin()">
                <router-link :to="{ name: 'my-absences' }">
                  <font-awesome-icon class="iconNav" icon="list" />
                  <span>Mis ausencias</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'benefits-search' }">
                  <font-awesome-icon class="iconNav" icon="heart" />
                  <span>Beneficios</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'my-benefits' }">
                  <font-awesome-icon class="iconNav" icon="heart" />
                  <span>Mis beneficios</span>
                </router-link>
              </li>
              <li v-if="hasRoleUser()">
                <router-link :to="{ name: 'calendar' }">
                  <font-awesome-icon class="iconNav" icon="calendar-alt" />
                  <span>Calendario</span>
                </router-link>
              </li>
              <li v-if="hasCoursesPermission()" class="course-menu-item">
                <router-link :to="{ name: 'training' }">
                  <img :src="require(`@/assets/icons/mortarboard.svg`)" alt="" class="course-menu-icon" />
                  <span>Cursos</span>
                </router-link>
              </li>
              <li v-if="hasCoursesPermission()" class="course-menu-item">
                <router-link :to="{ name: 'request-courses' }">
                  <img :src="require(`@/assets/icons/mortarboard.svg`)" alt="" class="course-menu-icon" />
                  <span>Solicitar cursos</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'employees' }">
                  <font-awesome-icon class="iconNav" icon="users" />
                  <span>Empleados</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'my-data' }">
                  <font-awesome-icon class="iconNav" icon="user" />
                  <span>Mis datos</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'manage-presence' }">
                  <font-awesome-icon class="iconNav" icon="list" />
                  <span>Gestión fichajes</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'presence-report' }">
                  <font-awesome-icon class="iconNav" icon="list" />
                  <span>Informe presencia</span>
                </router-link>
              </li>
              <li v-if="hasRoleUser()">
                <router-link :to="{ name: 'presence' }">
                  <svg id="chronometer" version="1.1" xmlns="http://www.w3.org/2000/svg" class="iconNav"
                    xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 233.766 233.766" style="
                      enable-background: new 0 0 233.766 233.766;
                      width: 18px;
                      fill: white;
                    " xml:space="preserve">
                    <g>
                      <path d="M188.169,79.423l6.059-6.058l2.906,2.906c1.465,1.465,3.384,2.197,5.304,2.197c1.919,0,3.839-0.732,5.303-2.196
                            c2.93-2.929,2.93-7.678,0.001-10.606l-16.419-16.421c-2.929-2.93-7.678-2.929-10.607-0.001c-2.93,2.929-2.93,7.678-0.001,10.607
                            l2.907,2.907l-6.06,6.059c-10.441-8.901-22.855-15.553-36.484-19.201c4.082-5.216,6.524-11.773,6.524-18.895
                            C147.602,13.781,133.821,0,116.883,0c-16.939,0-30.72,13.781-30.72,30.721c0,7.121,2.442,13.679,6.525,18.895
                            c-13.63,3.647-26.043,10.3-36.485,19.201l-6.059-6.059l2.907-2.907c2.929-2.929,2.928-7.678-0.001-10.607
                            c-2.93-2.928-7.678-2.929-10.607,0.001L26.024,65.666c-2.929,2.929-2.928,7.678,0,10.606c1.465,1.464,3.384,2.196,5.303,2.196
                            s3.839-0.732,5.304-2.197l2.906-2.906l6.058,6.058c-13.944,16.357-22.375,37.551-22.375,60.68
                            c0,51.646,42.017,93.663,93.662,93.663s93.662-42.017,93.662-93.663C210.545,116.974,202.113,95.78,188.169,79.423z
                            M101.163,30.721c0-8.668,7.052-15.721,15.72-15.721s15.72,7.053,15.72,15.721s-7.052,15.72-15.72,15.72
                            S101.163,39.389,101.163,30.721z M116.883,218.766c-43.375,0-78.662-35.288-78.662-78.663c0-43.374,35.288-78.662,78.662-78.662
                            c43.374,0,78.662,35.288,78.662,78.662C195.545,183.477,160.257,218.766,116.883,218.766z" />
                      <path d="M116.883,76.981c-4.142,0-7.5,3.357-7.5,7.5v55.621c0,4.143,3.358,7.5,7.5,7.5c4.143,0,7.5-3.357,7.5-7.5V84.481
                            C124.383,80.339,121.025,76.981,116.883,76.981z" />
                    </g>
                  </svg>
                  <span>Presencia</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'my-suggestions', params: { id: user.id } }">
                  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="22" viewBox="0 0 25 22" fill="none"
                    style="width: 21px">
                    <path
                      d="M6.60871 15.2678C6.78273 16.4707 6.94284 17.6247 7.1099 18.7786C7.15863 19.1143 7.20736 19.45 7.25608 19.7787C7.36746 20.569 7.00549 21.3663 6.28154 21.709C5.43925 22.1077 4.56216 22.1147 3.74772 21.6181C3.20476 21.2894 2.98201 20.7509 2.89847 20.1214C2.6966 18.5968 2.47385 17.0791 2.2511 15.5545C2.23718 15.4636 2.20933 15.3797 2.18845 15.2817C1.97962 15.2678 1.77775 15.2678 1.5898 15.2328C0.705747 15.0789 0.0374876 14.3795 0.0235656 13.4774C-0.00427858 11.8688 -0.0112396 10.2602 0.0235656 8.65165C0.0444487 7.63056 0.872812 6.87523 1.89608 6.86824C3.51801 6.86124 5.14689 6.87523 6.76881 6.86124C10.7853 6.83327 14.1754 5.35059 16.911 2.39222C17.461 1.79775 18.0039 1.1823 18.5469 0.580837C19.0411 0.0353224 19.6746 -0.132528 20.308 0.10526C20.9345 0.343048 21.3035 0.895556 21.3035 1.6369C21.3104 3.30141 21.3174 4.97293 21.3035 6.63744C21.3035 6.93118 21.3661 7.05008 21.6863 7.10603C23.6284 7.44173 25.0207 9.14121 24.9998 11.1065C24.9858 13.0717 23.5588 14.7292 21.6167 15.0509C21.5262 15.0649 21.4357 15.0859 21.3104 15.1139C21.3104 15.2608 21.3104 15.3936 21.3104 15.5335C21.3104 17.1281 21.3243 18.7297 21.3035 20.3243C21.2895 21.4992 20.2106 22.2475 19.1595 21.8139C18.9506 21.73 18.7627 21.5761 18.5956 21.4153C17.7742 20.611 17.0015 19.7508 16.1523 18.9814C13.521 16.5896 10.4025 15.3727 6.8593 15.2538C6.79665 15.2608 6.72704 15.2678 6.60871 15.2678ZM19.7929 20.5131C19.7929 14.1068 19.7929 7.81939 19.7929 1.53199C19.7651 1.518 19.7372 1.50401 19.7024 1.49003C19.6328 1.58095 19.5632 1.66487 19.4866 1.7488C19.0272 2.25934 18.5678 2.76989 18.1083 3.28043C15.7555 5.9031 12.8597 7.53265 9.40008 8.1411C9.14252 8.19006 9.06595 8.28797 9.06595 8.54674C9.07291 10.2043 9.07987 11.8548 9.05899 13.5123C9.05203 13.883 9.15645 14.0019 9.51146 14.0718C11.8991 14.5474 14.1336 15.4286 16.034 16.9742C17.2382 17.9534 18.3032 19.1003 19.4309 20.1704C19.5214 20.2613 19.6189 20.3522 19.7929 20.5131ZM7.57629 8.38589C7.53452 8.3649 7.50668 8.35092 7.4858 8.35092C5.65504 8.35092 3.82429 8.35092 1.98658 8.35791C1.61068 8.35791 1.48538 8.51177 1.48538 8.91042C1.48538 10.3302 1.48538 11.7499 1.48538 13.1766C1.48538 13.6802 1.60372 13.7921 2.09795 13.7921C3.58066 13.7921 5.06336 13.7921 6.55302 13.7921C6.88715 13.7921 7.22128 13.7921 7.57629 13.7921C7.57629 11.9737 7.57629 10.1763 7.57629 8.38589ZM21.3383 8.57472C21.3383 10.2532 21.3383 11.9177 21.3383 13.5753C22.4451 13.5473 23.4614 12.4353 23.5171 11.2114C23.5797 9.88955 22.6539 8.74257 21.3383 8.57472ZM3.71292 15.2678C3.72684 15.4286 3.7338 15.5475 3.75468 15.6594C3.94959 17.0162 4.1445 18.38 4.33941 19.7368C4.44382 20.4501 4.47863 20.4781 5.19562 20.4711C5.24434 20.4711 5.28611 20.4711 5.33484 20.4711C5.66897 20.4501 5.82907 20.2753 5.7873 19.9466C5.57847 18.4569 5.36268 16.9742 5.13993 15.4916C5.12601 15.4076 4.99375 15.2887 4.91021 15.2817C4.52736 15.2538 4.1445 15.2678 3.71292 15.2678Z"
                      fill="white" />
                  </svg>
                  <span>Mis sugerencias</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'suggestions-search' }">
                  <svg xmlns="http://www.w3.org/2000/svg" width="25" height="22" viewBox="0 0 25 22" fill="none"
                    style="width: 21px">
                    <path
                      d="M6.60871 15.2678C6.78273 16.4707 6.94284 17.6247 7.1099 18.7786C7.15863 19.1143 7.20736 19.45 7.25608 19.7787C7.36746 20.569 7.00549 21.3663 6.28154 21.709C5.43925 22.1077 4.56216 22.1147 3.74772 21.6181C3.20476 21.2894 2.98201 20.7509 2.89847 20.1214C2.6966 18.5968 2.47385 17.0791 2.2511 15.5545C2.23718 15.4636 2.20933 15.3797 2.18845 15.2817C1.97962 15.2678 1.77775 15.2678 1.5898 15.2328C0.705747 15.0789 0.0374876 14.3795 0.0235656 13.4774C-0.00427858 11.8688 -0.0112396 10.2602 0.0235656 8.65165C0.0444487 7.63056 0.872812 6.87523 1.89608 6.86824C3.51801 6.86124 5.14689 6.87523 6.76881 6.86124C10.7853 6.83327 14.1754 5.35059 16.911 2.39222C17.461 1.79775 18.0039 1.1823 18.5469 0.580837C19.0411 0.0353224 19.6746 -0.132528 20.308 0.10526C20.9345 0.343048 21.3035 0.895556 21.3035 1.6369C21.3104 3.30141 21.3174 4.97293 21.3035 6.63744C21.3035 6.93118 21.3661 7.05008 21.6863 7.10603C23.6284 7.44173 25.0207 9.14121 24.9998 11.1065C24.9858 13.0717 23.5588 14.7292 21.6167 15.0509C21.5262 15.0649 21.4357 15.0859 21.3104 15.1139C21.3104 15.2608 21.3104 15.3936 21.3104 15.5335C21.3104 17.1281 21.3243 18.7297 21.3035 20.3243C21.2895 21.4992 20.2106 22.2475 19.1595 21.8139C18.9506 21.73 18.7627 21.5761 18.5956 21.4153C17.7742 20.611 17.0015 19.7508 16.1523 18.9814C13.521 16.5896 10.4025 15.3727 6.8593 15.2538C6.79665 15.2608 6.72704 15.2678 6.60871 15.2678ZM19.7929 20.5131C19.7929 14.1068 19.7929 7.81939 19.7929 1.53199C19.7651 1.518 19.7372 1.50401 19.7024 1.49003C19.6328 1.58095 19.5632 1.66487 19.4866 1.7488C19.0272 2.25934 18.5678 2.76989 18.1083 3.28043C15.7555 5.9031 12.8597 7.53265 9.40008 8.1411C9.14252 8.19006 9.06595 8.28797 9.06595 8.54674C9.07291 10.2043 9.07987 11.8548 9.05899 13.5123C9.05203 13.883 9.15645 14.0019 9.51146 14.0718C11.8991 14.5474 14.1336 15.4286 16.034 16.9742C17.2382 17.9534 18.3032 19.1003 19.4309 20.1704C19.5214 20.2613 19.6189 20.3522 19.7929 20.5131ZM7.57629 8.38589C7.53452 8.3649 7.50668 8.35092 7.4858 8.35092C5.65504 8.35092 3.82429 8.35092 1.98658 8.35791C1.61068 8.35791 1.48538 8.51177 1.48538 8.91042C1.48538 10.3302 1.48538 11.7499 1.48538 13.1766C1.48538 13.6802 1.60372 13.7921 2.09795 13.7921C3.58066 13.7921 5.06336 13.7921 6.55302 13.7921C6.88715 13.7921 7.22128 13.7921 7.57629 13.7921C7.57629 11.9737 7.57629 10.1763 7.57629 8.38589ZM21.3383 8.57472C21.3383 10.2532 21.3383 11.9177 21.3383 13.5753C22.4451 13.5473 23.4614 12.4353 23.5171 11.2114C23.5797 9.88955 22.6539 8.74257 21.3383 8.57472ZM3.71292 15.2678C3.72684 15.4286 3.7338 15.5475 3.75468 15.6594C3.94959 17.0162 4.1445 18.38 4.33941 19.7368C4.44382 20.4501 4.47863 20.4781 5.19562 20.4711C5.24434 20.4711 5.28611 20.4711 5.33484 20.4711C5.66897 20.4501 5.82907 20.2753 5.7873 19.9466C5.57847 18.4569 5.36268 16.9742 5.13993 15.4916C5.12601 15.4076 4.99375 15.2887 4.91021 15.2817C4.52736 15.2538 4.1445 15.2678 3.71292 15.2678Z"
                      fill="white" />
                  </svg>
                  <span>Sugerencias</span>
                </router-link>
              </li>
              <li v-if="hasRoleUser()">
                <!-- TODO: coger el ID del usuario logeado -->
                <router-link :to="{ name: 'holiday', params: { id: user.id } }">
                  <font-awesome-icon class="iconNav" icon="umbrella-beach" />
                  <span>Mis vacaciones</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'holidays' }">
                  <font-awesome-icon class="iconNav" icon="umbrella-beach" />
                  <span>Vacaciones</span>
                </router-link>
              </li>
              <li v-if="teamStore.canAccessFor(user)">
                <router-link :to="{ name: 'my-team' }">
                  <font-awesome-icon class="iconNav" icon="users" />
                  <span>Vacaciones Equipo</span>
                </router-link>
              </li>
            </ul>
          </div>
        </Transition>
      </li>
      <!-- Producción -->
      <li v-if="
        (hasRoleAdmin() ||
          hasRoleSystems() ||
          hasTrackernetPermission() ||
          hasTrackernetReportPermission()) &&
        !isCollaborator
      " class="li-border li-group" :class="{ 'is-open': openProduccion }">
        <div class="menu-border" @click="showProduccion()">
          <img style="width: 25px; margin-right: 10px; margin-bottom: 5px" :src="!openProduccion
            ? require(`@/assets/icons/computer-blue.png`)
            : require(`@/assets/icons/computer-yellow.png`)
            " />
          <span class="pointer">PRODUCCIÓN</span>
        </div>
        <Transition name="sidebar-submenu" @before-enter="beforeSubmenuEnter" @enter="enterSubmenu"
          @after-enter="afterSubmenuEnter" @before-leave="beforeSubmenuLeave" @leave="leaveSubmenu"
          @after-leave="afterSubmenuLeave">
          <div v-if="openProduccion" class="submenu-border">
            <ul class="sub_menu">
              <li v-if="hasRoleSystems()">
                <router-link :to="{ name: 'clients' }">
                  <font-awesome-icon class="iconNav" icon="user" />
                  <span>Gestión de clientes</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'projects' }">
                  <font-awesome-icon class="iconNav" icon="users" />
                  <span>Gestión proyectos</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'collaborators' }">
                  <font-awesome-icon class="iconNav" icon="users" />
                  <span>Colaboradores</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'production' }">
                  <svg class="iconNav dashboard-people-kpi-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="16" rx="2" />
                    <circle cx="8" cy="10" r="2" />
                    <path d="M5.5 16c.6-1.7 4.4-1.7 5 0" />
                    <path d="M14 16V12" />
                    <path d="M17 16V9" />
                    <path d="M20 16V7" />
                  </svg>
                  <span>Dashboard</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'production-project-dashboard' }">
                  <font-awesome-icon class="iconNav" icon="chart-bar" />
                  <span>Dashboard proyectos</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'providers' }">
                  <font-awesome-icon class="iconNav" icon="list" />
                  <span>Proveedores</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin() || hasTrackernetReportPermission()">
                <router-link :to="{ name: 'tracker-report' }">
                  <img style="width: 16px; margin-bottom: 5px" :src="require(`@/assets/icons/tracker-net-white.svg`)" />
                  <span>Informes TrackerNet</span>
                </router-link>
              </li>
              <li v-if="hasTrackernetPermission() || hasRoleAdmin()">
                <router-link :to="{ name: 'tracker' }">
                  <img style="width: 16px; margin-bottom: 5px" :src="require(`@/assets/icons/tracker-net-white.png`)" />
                  <span>TrackerNet</span>
                </router-link>
              </li>
            </ul>
          </div>
        </Transition>
      </li>
      <!-- Hosting -->
      <li v-if="hasSystemsMenuAccess()" class="li-border li-group" :class="{ 'is-open': openHostings }">
        <div class="menu-border" @click="showHostings()">
          <img style="width: 25px; margin-right: 10px; margin-bottom: 5px" :src="!openHostings
            ? require(`@/assets/icons/server-blue.png`)
            : require(`@/assets/icons/server-yellow.png`)
            " />
          <span class="pointer">SISTEMAS</span>
        </div>
        <Transition name="sidebar-submenu" @before-enter="beforeSubmenuEnter" @enter="enterSubmenu"
          @after-enter="afterSubmenuEnter" @before-leave="beforeSubmenuLeave" @leave="leaveSubmenu"
          @after-leave="afterSubmenuLeave">
          <div v-if="openHostings" class="submenu-border">
            <ul class="sub_menu">
              <li v-if="hasRoleAdmin() || hasRoleSystems()">
                <router-link :to="{ name: 'domains-search' }">
                  <font-awesome-icon class="iconNav" icon="server" />
                  <span>Dominios</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin() || hasRoleSystems()" class="material-menu-item">
                <router-link :to="{ name: 'material-delivery' }">
                  <IconBlueBox style="margin: 0 !important" class="myIcons iconBlueBox" />
                  <span>Asignar material</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin() || hasRoleSystems()" class="material-menu-item">
                <router-link :to="{ name: 'material' }">
                  <IconBlueBox style="margin: 0 !important" class="myIcons iconBlueBox" />
                  <span>Material</span>
                </router-link>
              </li>
              <li v-if="hasRoleUser()" class="material-menu-item">
                <router-link :to="{ name: 'my-material' }">
                  <IconBlueBox style="margin: 0 !important" class="myIcons iconBlueBox" />
                  <span>Mi material</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin() || hasRoleSystems()" class="material-menu-item">
                <router-link :to="{ name: 'request-material' }">
                  <IconBlueBox style="margin: 0 !important" class="myIcons iconBlueBox" />
                  <span>Solicitar material</span>
                </router-link>
              </li>
              <!-- Tickets de Sistemas deshabilitado; se conserva para su futura reactivación. -->
              <li v-if="false">
                <router-link :to="{ name: 'tickets' }">
                  <font-awesome-icon class="iconNav" icon="server" />
                  <span>Tickets</span>
                </router-link>
              </li>
            </ul>
          </div>
        </Transition>
      </li>
      <!-- Comunicacion -->
      <li v-if="
        hasRoleAdmin() || hasInternalTicketsPermission() || hasVehicleModulePermission() || hasRoleUser()
      " class="li-border li-group" :class="{ 'is-open': openComunication }">
        <div class="menu-border" @click="showComunication()">
          <img style="width: 25px; margin-right: 10px; margin-bottom: 5px" :src="!openComunication
            ? require(`@/assets/icons/altavoz-menu.png`)
            : require(`@/assets/icons/altavoz-menu-yellow.png`)
            " />
          <span class="pointer">COMUNICACIÓN</span>
        </div>
        <Transition name="sidebar-submenu" @before-enter="beforeSubmenuEnter" @enter="enterSubmenu"
          @after-enter="afterSubmenuEnter" @before-leave="beforeSubmenuLeave" @leave="leaveSubmenu"
          @after-leave="afterSubmenuLeave">
          <div v-if="openComunication" class="submenu-border">
            <ul class="sub_menu">
              <li v-if="hasRoleUser()">
                <router-link :to="{ name: 'meeting-room-reservations' }">
                  <IconMeetingRoomReservation class="iconMeetingMenu" />
                  <span>Reserva de sala</span>
                </router-link>
              </li>
              <li v-if="hasInternalTicketsPermission()">
                <router-link :to="{ name: 'internal_tickets' }">
                  <img class="svg-white" style="width: 16px; margin-bottom: 5px"
                    :src="require(`@/assets/icons/server-blue.png`)" />
                  <span>Tickets internos</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()" class="undanews-menu-item">
                <router-link :to="{ name: 'undanews' }">
                  <svg class="iconNav undanews-news-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M4 5h13a2 2 0 0 1 2 2v12H6a2 2 0 0 1-2-2V5Z" />
                    <path d="M19 9h1a1 1 0 0 1 1 1v7a2 2 0 0 1-2 2" />
                    <path d="M7 9h5" />
                    <path d="M7 13h8" />
                    <path d="M7 16h6" />
                  </svg>
                  <span>Undanews</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'undanitas' }">
                  <!--  <font-awesome-icon class="iconNav" icon="users"/> -->
                  <img style="width: 16px; margin-bottom: 5px" :src="require(`@/assets/icons/tshirt.png`)" />
                  <span>Undanitas</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin() || hasVehicleModulePermission()">
                <router-link :to="{ name: 'vehicle-requests' }">
                  <IconVehicle class="iconVehicle" />
                  <span>Solicitudes vehículos</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'vehicles' }">
                  <IconVehicle class="iconVehicle" />
                  <span>Vehículos</span>
                </router-link>
              </li>
            </ul>
          </div>
        </Transition>
      </li>
      <!-- Administración -->
      <li v-if="hasRoleAdmin() || hasInvoiceModulePermission()" class="li-border li-group"
        :class="{ 'is-open': openAdministration }">
        <div class="menu-border" @click="showAdministration()">
          <IconOwnerReport class="administration-menu-icon" />
          <span class="pointer">ADMON</span>
        </div>
        <Transition name="sidebar-submenu" @before-enter="beforeSubmenuEnter" @enter="enterSubmenu"
          @after-enter="afterSubmenuEnter" @before-leave="beforeSubmenuLeave" @leave="leaveSubmenu"
          @after-leave="afterSubmenuLeave">
          <div v-if="openAdministration" class="submenu-border">
            <ul class="sub_menu">
              <li v-if="hasInvoiceModulePermission()">
                <router-link :to="{ name: 'invoices' }">
                  <svg style="width:16px;flex-shrink:0;" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-width="1.8" aria-hidden="true">
                    <rect x="4" y="2" width="16" height="20" rx="2" />
                    <path d="M8 7h8M8 11h8M8 15h5" />
                  </svg>
                  <span>Cobros</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'owner-report' }">
                  <IconOwnerReport class="administration-report-submenu-icon" />
                  <span>Informe propietario</span>
                </router-link>
              </li>
            </ul>
          </div>
        </Transition>
      </li>
      <!-- Seguridad -->
      <li v-if="hasAnySecurityPermission()" class="li-border li-group" :class="{ 'is-open': openSecurity }">
        <div class="menu-border" @click="showSecurity()">
          <svg class="security-menu-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2.5 20 6v5.2c0 5-3.4 8.7-8 10.3-4.6-1.6-8-5.3-8-10.3V6l8-3.5Z" />
            <path d="M8.8 11.7 11 13.9l4.5-4.5" />
          </svg>
          <span class="pointer">SEGURIDAD</span>
        </div>
        <Transition name="sidebar-submenu" @before-enter="beforeSubmenuEnter" @enter="enterSubmenu"
          @after-enter="afterSubmenuEnter" @before-leave="beforeSubmenuLeave" @leave="leaveSubmenu"
          @after-leave="afterSubmenuLeave">
          <div v-if="openSecurity" class="submenu-border">
            <ul class="sub_menu">
              <li v-if="hasSecurityApiApplicationsPermission()">
                <router-link :to="{ name: 'security-api-applications' }">
                  <font-awesome-icon class="iconNav" icon="key" />
                  <span>Aplicaciones API</span>
                </router-link>
              </li>
              <li v-if="hasSecurityAuditPermission()">
                <router-link :to="{ name: 'security-audit' }">
                  <font-awesome-icon class="iconNav" icon="arrows-rotate" />
                  <span>Auditoría</span>
                </router-link>
              </li>
              <li v-if="hasRoleAdmin()">
                <router-link :to="{ name: 'security-configuration' }">
                  <font-awesome-icon class="iconNav" icon="shield-alt" />
                  <span>Configuración</span>
                </router-link>
              </li>
              <li v-if="hasImpersonationsPermission()">
                <router-link :to="{ name: 'security-impersonations' }">
                  <font-awesome-icon class="iconNav" icon="user-secret" />
                  <span>Impersonaciones</span>
                </router-link>
              </li>
              <li v-if="hasSecurityPermissionsModulePermission()">
                <router-link :to="{ name: 'security-permission-catalog' }">
                  <font-awesome-icon class="iconNav" icon="list" />
                  <span>Catálogo de permisos</span>
                </router-link>
              </li>
              <li v-if="hasSecurityPermissionsModulePermission()">
                <router-link :to="{ name: 'security-permission-profiles' }">
                  <font-awesome-icon class="iconNav" icon="user-tie" />
                  <span>Perfiles de permisos</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'security' }">
                  <svg class="iconNav security-menu-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2.5 20 6v5.2c0 5-3.4 8.7-8 10.3-4.6-1.6-8-5.3-8-10.3V6l8-3.5Z" />
                    <path d="M8.8 11.7 11 13.9l4.5-4.5" />
                  </svg>
                  <span>Resumen</span>
                </router-link>
              </li>
              <li v-if="hasSecurityPermissionsModulePermission()">
                <router-link :to="{ name: 'security-external-users' }">
                  <font-awesome-icon class="iconNav" icon="user-friends" />
                  <span>Usuarios externos</span>
                </router-link>
              </li>
              <li v-if="hasSecurityPermissionsModulePermission()">
                <router-link :to="{ name: 'security-internal-users' }">
                  <font-awesome-icon class="iconNav" icon="users" />
                  <span>Usuarios internos</span>
                </router-link>
              </li>
            </ul>
          </div>
        </Transition>
      </li>
      <!-- Fondo social -->
      <li v-if="hasSocialFundPermission() || hasSocialFundManagePermission()"
        class="li-border li-group" :class="{ 'is-open': openSocialFund }">
        <div class="menu-border" @click="showSocialFund()">
          <svg class="social-fund-menu-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M12 20.5S4 16.1 4 10.1C4 7.7 5.8 6 8.2 6c1.5 0 2.9.8 3.8 2 1-1.2 2.3-2 3.8-2 2.4 0 4.2 1.7 4.2 4.1 0 6-8 10.4-8 10.4Z" />
            <path d="M7.5 14.3h9" />
            <path d="M12 11.8v5" />
          </svg>
          <span class="pointer">FONDO SOCIAL</span>
        </div>
        <Transition name="sidebar-submenu" @before-enter="beforeSubmenuEnter" @enter="enterSubmenu"
          @after-enter="afterSubmenuEnter" @before-leave="beforeSubmenuLeave" @leave="leaveSubmenu"
          @after-leave="afterSubmenuLeave">
          <div v-if="openSocialFund" class="submenu-border">
            <ul class="sub_menu">
              <li v-if="hasSocialFundManagePermission()">
                <router-link :to="{ name: 'social-fund-calls' }">
                  <svg class="iconNav social-fund-call-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="4" y="3" width="16" height="18" rx="2" />
                    <path d="M8 8h8M8 12h8M8 16h5" />
                  </svg>
                  <span>Convocatorias</span>
                </router-link>
              </li>
              <li v-if="hasSocialFundPermission()">
                <router-link :to="{ name: 'social-fund-requests' }">
                  <svg class="iconNav social-fund-call-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M4 20h16" />
                    <path d="M6 17V7l6-4 6 4v10" />
                    <path d="M9 20v-5h6v5" />
                    <path d="M9 10h.01M15 10h.01" />
                  </svg>
                  <span>Solicitudes</span>
                </router-link>
              </li>
            </ul>
          </div>
        </Transition>
      </li>
      <!-- Configuración -->
      <li v-if="hasRoleAdmin()" class="li-border li-group" :class="{ 'is-open': openSettings }">
        <div class="menu-border" @click="showSettings()">
          <svg class="section-gear-icon" :class="{ active: settingsHover }" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M19.4 13.5c.1-.5.1-1 .1-1.5s0-1-.1-1.5l2-1.5-2-3.4-2.4 1a7.7 7.7 0 0 0-2.6-1.5L14 2.5h-4l-.4 2.6A7.7 7.7 0 0 0 7 6.6l-2.4-1-2 3.4 2 1.5A8.8 8.8 0 0 0 4.5 12c0 .5 0 1 .1 1.5l-2 1.5 2 3.4 2.4-1a7.7 7.7 0 0 0 2.6 1.5l.4 2.6h4l.4-2.6a7.7 7.7 0 0 0 2.6-1.5l2.4 1 2-3.4-2-1.5Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          <span class="pointer">CONFIGURACIÓN</span>
        </div>
        <Transition name="sidebar-submenu" @before-enter="beforeSubmenuEnter" @enter="enterSubmenu"
          @after-enter="afterSubmenuEnter" @before-leave="beforeSubmenuLeave" @leave="leaveSubmenu"
          @after-leave="afterSubmenuLeave">
          <div v-if="openSettings" class="submenu-border">
            <ul class="sub_menu">
              <li>
                <router-link :to="{ name: 'absence-types' }">
                  <font-awesome-icon class="iconNav" icon="first-aid" />
                  <span>Motivos de ausencia</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'leave-types' }">
                  <font-awesome-icon class="iconNav" icon="procedures" />
                  <span>Tipos de baja</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'holiday-calendar' }">
                  <font-awesome-icon class="iconNav" icon="calendar-alt" />
                  <span>Calendario de festivos</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'schedules' }">
                  <font-awesome-icon class="iconNav" icon="calendar-alt" />
                  <span>Horarios</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'workplaces' }">
                  <font-awesome-icon class="iconNav" icon="building" />
                  <span>Centro de trabajo</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'departments' }">
                  <font-awesome-icon class="iconNav" icon="list" />
                  <span>Departamentos</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'jobs' }">
                  <font-awesome-icon class="iconNav" icon="users" />
                  <span>Puestos de trabajo</span>
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'holiday-incompatibilities' }">
                  <font-awesome-icon class="iconNav" icon="umbrella-beach" />
                  <span>Vacaciones incompatibles</span>
                </router-link>
              </li>
            </ul>
          </div>
        </Transition>
      </li>
      <li class="li-border li-group li-small" v-if="!isCollaborator">
        <div>
          <router-link :to="{ name: 'change-password' }">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 724 1024"
              style="width: 25px; margin-right: 10px; margin-bottom: 5px">
              <path
                d="M0 499.8l59-181.7 332.5 108.6-59 180.8-332.5-107.7zM79.6 744.3l206.1-282.9 154.5 111.5-206.1 283.8-154.5-112.4zM266.9 167.3h191.1v350.3h-191.1v-350.3zM285.6 572.9l154.5-111.5 206.1 282.9-154.5 112.4-206.1-283.8zM332.5 426.8l332.5-108.6 59 181.7-331.6 107.7-59.9-180.8z"
                fill="#474ce6" />
            </svg>
            <span class="pointer label">CAMBIAR CONTRASEÑA</span>
          </router-link>
        </div>
      </li>
      <li class="li-border li-group li-small" v-if="!isCollaborator">
        <div @click="logout()">
          <el-icon class="sidebar-main-icon">
            <TurnOff style="
                color: #474ce6;
                margin: 0px !important;
                width: 25px !important;
                height: 25px !important;
              " />
          </el-icon>
          <span class="pointer label">SALIR</span>
        </div>
      </li>

      <!--      <li class="li-border li-group" v-if="hasRoleAdmin()">-->
      <!--        <div>-->
      <!--          <button @click="changeNav()" class="rounded-circle border-0" id="sidebarToggle">-->
      <!--            <font-awesome-icon class="iconNav" :icon="nav_desp ? 'angle-left' : 'angle-right'"/>-->
      <!--          </button>-->
      <!--        </div>-->

      <!--      </li>-->
    </ul>
  </div>
</template>

<style lang="scss" scoped>
#nav {
  position: fixed;
  padding: 1.25rem 10px 1.25rem;
  height: calc(100% - 70px);
  left: 0px;
  top: 70px;
  background: radial-gradient(circle at 20% 8%, rgba(59, 83, 98, 0.28), transparent 34%),
    linear-gradient(160deg, #17232c 0%, #101820 52%, #0b141a 100%);
  box-shadow: inset -1px 0 0 rgba(255, 255, 255, 0.08), 12px 0 34px rgba(0, 0, 0, 0.18);
  transition: width 0.6s ease, transform 0.6s ease;
  will-change: width, transform;
  z-index: 100;
  overflow: hidden auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  div.user {
    position: relative;
    width: 100%;
    text-align: left;
    padding: 5px 6px 15px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.16);

    .img-user.closed {
      display: flex;
      justify-content: center;
      transform: translate(8px, -3px);
      transition-delay: 0.45s;

      .thumbnail {
        width: 58px;
        height: 58px;
        margin-right: 0;
      }
    }

    .username.closed {
      position: absolute;
      left: 78px;
      width: 150px;
      opacity: 0;
      transform: translateX(-8px);
      visibility: hidden;
      transition: opacity 0.6s ease, transform 0.6s ease, visibility 0s linear 0.6s;
    }
  }

  ul.main-menu span {
    opacity: 1;
    max-width: 100%;
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
    white-space: normal;
    overflow-wrap: break-word;
    word-break: normal;
    transform: translateX(0);
    visibility: visible;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    transition: opacity 0.6s ease, max-width 0.6s ease, margin-left 0.6s ease,
      transform 0.6s ease, visibility 0s linear 0s;
  }

  .username {
    opacity: 1;
    width: 150px;
    max-width: 150px;
    flex: 0 0 150px;
    overflow: hidden;
    min-width: 0;
    transform: translateX(0);
    visibility: visible;
    transition: opacity 0.6s ease, transform 0.6s ease, visibility 0s linear 0s;

    p {
      margin: 0 0 6px;
      line-height: 1.25;
      white-space: normal;
      overflow-wrap: anywhere;
    }

    p:last-child {
      margin-bottom: 0;
    }
  }

  ul.main-menu span {
    display: -webkit-box;
  }

  &.is-collapsed {
    ul.main-menu span {
      max-width: 0;
      margin-left: 0;
      opacity: 0;
      transform: translateX(-8px);
      visibility: hidden;
      transition: opacity 0.6s ease, max-width 0.6s ease, margin-left 0.6s ease,
        transform 0.6s ease, visibility 0s linear 0.6s;
    }
  }

  .thumbnail {
    width: 58px;
    height: 58px;
    min-width: 58px;
    max-width: 58px;
    min-height: 58px;
    max-height: 58px;
    flex: 0 0 58px;
    padding: 0;
    background-color: #fff;
    margin: 0 14px 0 0;
    object-fit: cover;
    object-position: center;
    box-shadow: 0 0 0 3px #fff;
    border: 2px solid rgba(255, 255, 255, 0.75);
  }

  .badge {
    border-radius: 0.5rem;
    padding: 0.25em 1em;
  }

  ul.main-menu {
    --sidebar-main-icon-size: 25px;
    --sidebar-main-gap: 10px;
    --sidebar-sub-icon-size: 20px;
    --sidebar-sub-gap: 8px;

    width: 100%;
    padding: 0;
    margin: 0;

    li {
      position: relative;
      width: 100%;
      height: auto;
      min-height: 0;
      margin-bottom: 0;
      text-align: left;
      list-style: none;

      .iconNav {
        color: white;
      }

      .iconVehicle,
      .iconHatGraduation {
        margin: 0 !important;
        width: 18px;
        fill: white;
      }

      .iconMeetingMenu {
        margin: 0 !important;
        width: 18px;
        height: 18px;
        min-width: 18px;
        min-height: 18px;
        max-width: 18px;
        max-height: 18px;
        flex-shrink: 0;
        fill: white;
      }

      .administration-menu-icon {
        color: #474ce6;
        height: 25px;
        margin-bottom: 5px;
        margin-right: 10px;
        width: 25px;
      }

      .administration-report-submenu-icon {
        color: white;
        height: 16px;
        margin-bottom: 5px;
        width: 16px;
      }

      .social-fund-menu-icon {
        fill: none;
        height: 25px;
        margin-bottom: 5px;
        margin-right: 10px;
        stroke: #474ce6;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 1.8;
        width: 25px;
      }

      .social-fund-call-icon {
        fill: none;
        stroke: currentColor;
        stroke-linecap: round;
        stroke-linejoin: round;
        stroke-width: 1.8;
      }

      .myIcons {
        margin-right: 2px !important;
        margin-left: 4px !important;
        margin-top: 1px !important;
        width: 20px;
        fill: white;
      }

      img {
        max-width: 80%;
        height: auto;
      }

      span {
        font-size: 15px;
        font-weight: 700;
        color: white;
        line-height: 1.22;
        margin-left: 0;
        letter-spacing: 0;
        transition: color 0.22s ease;

        &:hover.label {
          color: $yellow-color;
        }
      }

      button {
        width: 2.5rem;
        height: 2.5rem;
        text-align: center;
        cursor: pointer;
        background-color: rgba(255, 255, 255, 0.2);
        margin: auto;
        margin-top: 1rem;
        display: inline-flex;
        justify-content: center;
        align-items: center;

        .iconNav {
          color: white;
        }
      }

      ul.sub_menu {
        position: relative;
        padding: 10px 0 12px;
        margin: 0;

        li {
          margin: 0 !important;
          margin-top: 0;
          min-height: 0;
          position: relative;
        }

        a {
          display: grid;
          grid-template-columns: var(--sidebar-sub-icon-size) minmax(0, 1fr);
          align-items: center;
          justify-content: flex-start;
          column-gap: var(--sidebar-sub-gap);
          position: relative;
          box-sizing: border-box;
          width: 100%;
          min-height: 34px;
          padding: 3px 6px 3px 28px !important;
          text-decoration: none;

          &::before {
            content: "";
            position: absolute;
            left: 24px;
            top: 50%;
            z-index: 3;
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background: $yellow-color;
            box-shadow: 0 0 0 2px rgba(255, 214, 30, 0.16);
            transform: translate(-50%, -50%);
          }

          svg,
          img {
            margin: 0 !important;
            flex: 0 0 20px;
            width: 20px;
            max-width: 20px;
            min-width: 20px;
            color: white;
          }

          >svg,
          >img,
          >.svg-inline--fa,
          >.iconHatGraduation,
          >.myIcons {
            margin: 0 !important;
            flex: 0 0 20px !important;
            width: 20px !important;
            min-width: 20px !important;
            max-width: 20px !important;
            justify-self: center;
          }

          >.iconVehicle,
          >.iconMeetingMenu {
            margin: 0 !important;
            justify-self: center;
          }

          >.iconVehicle {
            width: 17px !important;
            min-width: 17px !important;
            max-width: 17px !important;
            height: 17px !important;
          }

          >.iconMeetingMenu {
            width: 18px !important;
            min-width: 18px !important;
            max-width: 18px !important;
            height: 18px !important;
          }

          >.course-menu-icon {
            filter: invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%);
          }

          >.dashboard-people-kpi-icon {
            fill: none;
            stroke: currentColor;
            stroke-width: 1.8;
            stroke-linecap: round;
            stroke-linejoin: round;
          }
        }

        span {
          font-size: 14px;
          font-weight: 500;
          text-transform: none;
          color: #f5f8fb;
          margin-left: 0;
        }

        span:hover {
          color: $yellow-color;
        }
      }
    }

    .li-group {
      height: auto;
      margin-top: 0;

      div {
        display: grid;
        grid-template-columns: var(--sidebar-main-icon-size) minmax(0, 1fr);
        justify-content: initial;
        text-align: left;
        padding-left: 0;
        align-items: center;
        column-gap: var(--sidebar-main-gap);

        >a {
          display: grid;
          grid-template-columns: var(--sidebar-main-icon-size) minmax(0, 1fr);
          grid-column: 1 / -1;
          justify-content: initial;
          text-align: left;
          align-items: center;
          column-gap: var(--sidebar-main-gap);
          min-width: 0;
        }

        .title-group {
          font-size: 18px;
          color: white;
          text-transform: uppercase;
          line-height: 3.77rem;
          margin-left: 10px;
          -webkit-transition: 0.6s;
          transition: 0.6s;
        }
      }
    }

    .li-border {
      border-top: 1px solid rgba(255, 255, 255, 0.13);
    }

    >li:first-child {
      border-top: 0;
    }

    .li-border:hover>div:first-child,
    .li-group.is-open>.menu-border,
    .li-group.list-menu.is-open>div:first-child {
      background: linear-gradient(90deg,
          rgba(255, 214, 30, 0.13),
          rgba(255, 255, 255, 0.08));
      color: $yellow-color;
      box-shadow: inset 4px 0 0 $yellow-color, inset 0 0 0 1px rgba(255, 255, 255, 0.06);
    }

    .li-border:hover>div:first-child span,
    .li-group.is-open>.menu-border span,
    .li-group.list-menu.is-open>div:first-child span {
      color: $yellow-color;
    }

    .li-border:hover>div:first-child .social-fund-menu-icon,
    .li-group.is-open>.menu-border .social-fund-menu-icon {
      stroke: $yellow-color;
    }
  }

  .img-user {
    display: inline-block;
    position: relative;
    transform: translateY(-3px);
    transition: transform 0.6s ease;
    transition-delay: 0s;

    &::before {
      content: "";
      position: absolute;
      width: 15px;
      height: 15px;
      margin: 0px;
      shape-outside: circle();
      clip-path: circle();
      background: $yellow-color;
      right: 10px;
      top: 4px;
    }
  }

  // width
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    cursor: pointer !important;
  }

  // Track
  &::-webkit-scrollbar-track {
    background: black;
    border-radius: 100px;
  }

  // Handle
  &::-webkit-scrollbar-thumb {
    background-color: rgba(128, 128, 128, 0.6);
    border-radius: 100px;
    width: 8px;
    height: 8px;
    transition: background-color 0.6s linear;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(128, 128, 128, 0.8);
  }

  .toggle-button {
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;
    color: white;
    font-size: 20px;
    z-index: 201;

    &:hover {
      color: $yellow-color;
    }
  }
}

#nav .main-menu>.li-group>div:first-child {
  position: relative;
  min-height: 52px;
  padding: 0 34px 0 12px;
  border-radius: 4px;
  cursor: pointer;
  display: grid;
  grid-template-columns: var(--sidebar-main-icon-size) minmax(0, 1fr);
  column-gap: var(--sidebar-main-gap);
  align-items: center;
  transition: background 0.22s ease, box-shadow 0.22s ease, color 0.22s ease;
}

#nav .main-menu>.li-group>div:first-child>img,
#nav .main-menu>.li-group>div:first-child>svg,
#nav .main-menu>.li-group>div:first-child>.svg-inline--fa,
#nav .main-menu>.li-group>div:first-child>.el-icon,
#nav .main-menu>.li-group>div:first-child>a>img,
#nav .main-menu>.li-group>div:first-child>a>svg,
#nav .main-menu>.li-group>div:first-child>a>.svg-inline--fa,
#nav .main-menu>.li-group>div:first-child>a>.el-icon {
  align-self: center;
  justify-self: center;
  flex: 0 0 var(--sidebar-main-icon-size);
  width: var(--sidebar-main-icon-size) !important;
  min-width: var(--sidebar-main-icon-size) !important;
  max-width: var(--sidebar-main-icon-size) !important;
  height: var(--sidebar-main-icon-size);
  margin: 0 !important;
  object-fit: contain;
}

.section-gear-icon {
  width: 25px;
  min-width: 25px;
  height: 25px;
  margin: 0 !important;
  fill: none;
  stroke: #474ce6;
  stroke-width: 1.9;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: stroke 0.22s ease;
}

.section-gear-icon.active,
.li-border:hover>div:first-child .section-gear-icon {
  stroke: $yellow-color;
}

.security-menu-icon {
  fill: none;
  stroke: #474ce6;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
  transition: stroke 0.22s ease;
}

.li-border:hover>div:first-child .security-menu-icon,
.li-group.is-open>div:first-child .security-menu-icon {
  stroke: $yellow-color;
}

#nav .sub_menu .security-menu-icon {
  stroke: #fff;
}

#nav .main-menu>.li-group>.menu-border::after,
#nav .main-menu>.li-group.has-submenu>div:first-child::after {
  content: "\203A";
  position: absolute;
  right: 15px;
  top: 43%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.86);
  font-size: 31px;
  line-height: 1;
  transition: transform 0.26s ease, top 0.26s ease, color 0.22s ease;
}

#nav .main-menu>.li-group.is-open>.menu-border::after,
#nav .main-menu>.li-group.has-submenu.is-open>div:first-child::after {
  top: 47%;
  color: $yellow-color;
  transform: translateY(-50%) rotate(90deg);
}

#nav .main-menu>.li-group.list-menu>div:first-child a {
  display: grid;
  grid-template-columns: var(--sidebar-main-icon-size) minmax(0, 1fr);
  grid-column: 1 / -1;
  column-gap: var(--sidebar-main-gap);
  align-items: center;
  width: 100%;
  min-height: inherit;
  text-decoration: none;
  min-width: 0;
}

#nav .main-menu>.li-group.list-menu:not(.has-submenu)>div:first-child {
  min-height: 48px;
}

#nav ul.main-menu li ul.sub_menu {
  padding: 10px 0 12px !important;
  margin: 0 !important;
}

#nav ul.main-menu li ul.sub_menu li {
  margin: 0 !important;
}

#nav ul.main-menu li ul.sub_menu li a {
  display: grid;
  grid-template-columns: var(--sidebar-sub-icon-size) minmax(0, 1fr);
  column-gap: var(--sidebar-sub-gap);
  align-items: center;
  padding: 3px 6px 3px 40px !important;
}

#nav ul.main-menu li ul.sub_menu li a svg,
#nav ul.main-menu li ul.sub_menu li a img {
  margin: 0 !important;
  justify-self: center;
  width: var(--sidebar-sub-icon-size) !important;
  height: var(--sidebar-sub-icon-size) !important;
  max-width: var(--sidebar-sub-icon-size) !important;
  min-width: var(--sidebar-sub-icon-size) !important;
  object-fit: contain;
}

#nav ul.main-menu li ul.sub_menu li.course-menu-item a .course-menu-icon {
  width: var(--sidebar-sub-icon-size) !important;
  height: var(--sidebar-sub-icon-size) !important;
  max-width: var(--sidebar-sub-icon-size) !important;
  min-width: var(--sidebar-sub-icon-size) !important;
  object-fit: contain;
}

#nav ul.main-menu li ul.sub_menu li a>*:first-child {
  margin: 0 !important;
  justify-self: center;
  flex: 0 0 var(--sidebar-sub-icon-size) !important;
  width: var(--sidebar-sub-icon-size) !important;
  max-width: var(--sidebar-sub-icon-size) !important;
  min-width: var(--sidebar-sub-icon-size) !important;
}

#nav ul.main-menu li ul.sub_menu li a>.iconVehicle,
#nav ul.main-menu li ul.sub_menu li a>.iconMeetingMenu {
  flex: 0 0 18px !important;
  justify-self: center;
  margin: 0 !important;
  width: 18px !important;
  min-width: 18px !important;
  max-width: 18px !important;
  height: 18px !important;
}

#nav ul.main-menu li ul.sub_menu li a>.iconVehicle {
  flex-basis: 17px !important;
  width: 17px !important;
  min-width: 17px !important;
  max-width: 17px !important;
  height: 17px !important;
}

#nav ul.main-menu li ul.sub_menu li.material-menu-item a {
  padding-left: 40px !important;
}

#nav ul.main-menu li ul.sub_menu li.material-menu-item a .iconBlueBox {
  transform: translateX(0);
}

#nav ul.main-menu li ul.sub_menu li.material-menu-item a span {
  margin-left: 0;
}

#nav ul.main-menu li ul.sub_menu li.undanews-menu-item a img {
  flex: 0 0 20px !important;
  width: 16px !important;
  max-width: 16px !important;
  min-width: 16px !important;
  object-fit: contain;
}

#nav ul.main-menu li ul.sub_menu li.undanews-menu-item a .undanews-news-icon {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.sidebar-submenu-enter-active,
.sidebar-submenu-leave-active {
  overflow: hidden;
}

.submenu-border {
  display: block !important;
  position: relative;
  overflow: hidden;
  transform-origin: top;
  margin: 0 3px 10px;
  border-radius: 0 0 4px 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-top: 0;
  background: rgba(8, 18, 24, 0.44);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.submenu-border::before {
  content: "";
  position: absolute;
  left: 24px;
  top: 27px;
  bottom: 29px;
  z-index: 1;
  width: 1px;
  background: $yellow-color;
}

.svg-white {
  filter: invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%);
}

@media (max-width: 992px) {
  #nav ul.main-menu li button {
    display: none;
  }

  // #nav {
  //   width: 110px !important;
  // }

  .toggle-button {
    display: none;
  }
}

@media (min-width: 992px) {
  .li-small {
    display: none;
  }
}
</style>
